/**
 * Development-aware logging utility.
 *
 * Provides structured logging with environment-aware behavior:
 * - Debug, info, and warn logs only appear in development
 * - Error logs always appear for debugging production issues
 * - Includes timestamps and emoji indicators for better readability
 * - Type-safe with proper TypeScript interfaces
 */

/** Available log levels with their display behavior */
type LogLevel = 'info' | 'warn' | 'error' | 'debug';

/** Structure of a formatted log entry */
interface LogEntry {
	/** Severity level of the log entry */
	level: LogLevel;
	/** Human-readable log message */
	message: string;
	/** Optional data payload to include */
	data?: any;
	/** ISO timestamp of when the log was created */
	timestamp: string;
}

/**
 * Logger class providing environment-aware structured logging
 */
class Logger {
	private isDevelopment = import.meta.env.DEV;

	/**
	 * Format a log message into a structured entry
	 * @param level - Log level
	 * @param message - Human-readable message
	 * @param data - Optional data to include
	 * @returns Formatted log entry
	 */
	private formatMessage(
		level: LogLevel,
		message: string,
		data?: any
	): LogEntry {
		return {
			level,
			message,
			data,
			timestamp: new Date().toISOString(),
		};
	}

	/**
	 * Determine if a log should be displayed based on environment and level
	 * @param level - Log level to check
	 * @returns Whether the log should be displayed
	 */
	private shouldLog(level: LogLevel): boolean {
		if (level === 'error') return true; // Always log errors
		return this.isDevelopment;
	}

	/**
	 * Log informational messages (development only)
	 * @param message - Message to log
	 * @param data - Optional data to include
	 */
	info(message: string, data?: any) {
		if (this.shouldLog('info')) {
			const entry = this.formatMessage('info', message, data);
			console.log(`🔵 ${entry.message}`, data || '');
		}
	}

	/**
	 * Log warning messages (development only)
	 * @param message - Message to log
	 * @param data - Optional data to include
	 */
	warn(message: string, data?: any) {
		if (this.shouldLog('warn')) {
			const entry = this.formatMessage('warn', message, data);
			console.warn(`🟡 ${entry.message}`, data || '');
		}
	}

	/**
	 * Log error messages (always shown)
	 * @param message - Message to log
	 * @param error - Optional error object or data to include
	 */
	error(message: string, error?: any) {
		if (this.shouldLog('error')) {
			const entry = this.formatMessage('error', message, error);
			console.error(`🔴 ${entry.message}`, error || '');
		}
	}

	/**
	 * Log debug messages (development only)
	 * @param message - Message to log
	 * @param data - Optional data to include
	 */
	debug(message: string, data?: any) {
		if (this.shouldLog('debug')) {
			const entry = this.formatMessage('debug', message, data);
			console.log(`🐛 ${entry.message}`, data || '');
		}
	}
}

/**
 * Singleton logger instance for use throughout the application
 *
 * @example
 * ```tsx
 * import { logger } from '../utils/logger';
 *
 * // Development only
 * logger.debug('User clicked button', { buttonId: 'submit' });
 * logger.info('Component mounted successfully');
 * logger.warn('Deprecated API usage detected');
 *
 * // Always shown
 * logger.error('Failed to fetch data', error);
 * ```
 */
export const logger = new Logger();
